import { t } from '@lingui/core/macro'

import type { Offer } from '@jotta/grpc-connect-openapi/offer'
import type { Offer_ProductFeature } from '@jotta/grpc-connect-openapi/offer'
import { Offer_ProductFeature_Type } from '@jotta/grpc-connect-openapi/offer'
import { RecurringSubscriptionInterval } from '@jotta/grpc-connect-openapi/payment'
import { useLocale } from '@jotta/i18n'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import SvgCheck from '@jotta/ui/SvgCheck'
import SvgLogoMain from '@jotta/ui/SvgLogo_mainLight'
import SvgWarningCircle from '@jotta/ui/SvgWarningCircle'
import { cn } from '@jotta/utils/css'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { formatPriceConnectWithInterval } from '@jotta/utils/formatPrice'

import { successUrl } from '../auth/PaidSignup'
import { PaymentInformation } from '../payment/PaymentInformation'
import { PaymentLayout } from '../payment/PaymentLayout'
import type { OfferState } from '../payment/useOfferState'
import { useOfferState } from '../payment/useOfferState'
import { usePaymentInfo } from '../payment/usePaymentInformation'

import { ProductNotFound } from './ProductNotFound'
import { useGetOffers } from './useGetOffers'

function isValid(offer: Offer) {
  const status = offer.status
  switch (status.case) {
    case 'currentSubscription':
    case 'valid':
    case 'recommended':
      return true
    case undefined:
    case 'businessUserNotPermitted':
    case 'invalid':
    case 'usersExceeded':
    case 'storageExceeded':
      return false
    default:
      return exhaustiveGuard(status)
  }
}

function isDisplayable(feature: Offer_ProductFeature) {
  const type = feature.type
  switch (type) {
    case Offer_ProductFeature_Type.UNKNOWN_PRODUCT_FEATURE:
    case Offer_ProductFeature_Type.OTHER:
      return false
    case Offer_ProductFeature_Type.DISCLAIMER:
    case Offer_ProductFeature_Type.STORAGE:
    case Offer_ProductFeature_Type.USER_COUNT:
    case Offer_ProductFeature_Type.COMMERCIAL_LICENSE:
      return true
    default:
      return exhaustiveGuard(type)
  }
}

function SelectedProduct({ offer }: { offer: Offer }) {
  const locale = useLocale()

  const { productCode, productNameLocalized, monthly, productFeatures } = offer
  const monthlyBasePrice = monthly?.basePrice
  return (
    <div className="mt-8">
      <label
        key={productCode}
        className={cn(
          'card mb-4 grid grid-cols-[1fr_1fr] items-center gap-4 rounded-md bg-signup-background-fill-radio py-4',
        )}
      >
        <div className="flex flex-col">
          <header className="label-lg">{productNameLocalized}</header>
          <div>
            {monthlyBasePrice &&
              formatPriceConnectWithInterval(
                RecurringSubscriptionInterval.MONTHLY_RECURRING,
                monthlyBasePrice,
                locale,
              )}
          </div>
        </div>
        <div className="flex flex-col">
          {productFeatures
            .filter(isDisplayable)
            .map(({ type, labelLocalized }) => (
              <div
                key={labelLocalized}
                className={cn('flex items-center gap-2', {
                  'text-danger': type === Offer_ProductFeature_Type.DISCLAIMER,
                })}
              >
                <PlainBrandIcon
                  icon={
                    type === Offer_ProductFeature_Type.DISCLAIMER
                      ? SvgWarningCircle
                      : SvgCheck
                  }
                  height={16}
                  className="shrink-0"
                />
                {labelLocalized}
              </div>
            ))}
        </div>
      </label>
    </div>
  )
}

export function ConvertPure({ offerState }: { offerState: OfferState }) {
  const { offer, productCode, interval, currentPaymentInfo } = offerState

  const conversion =
    currentPaymentInfo?.changeSubscriptionOption.case === 'convertFromReseller'

  if (!offer) {
    return (
      <ProductNotFound
        customerGroupOverride={conversion ? 'CUST' : undefined}
      />
    )
  }

  return (
    <PaymentLayout
      backPath="/web"
      backElement={<PlainBrandIcon icon={SvgLogoMain} height="38px" />}
      offer={offer}
      interval={interval}
      title={
        conversion
          ? t`Become a Jottacloud customer`
          : t`Renew your subscription`
      }
    >
      {summary => (
        <>
          {currentPaymentInfo?.productCode !== productCode && (
            <SelectedProduct offer={offer} />
          )}
          <PaymentInformation
            offerState={offerState}
            summary={summary}
            successUrl={successUrl(offer, interval, false)}
          />
        </>
      )}
    </PaymentLayout>
  )
}

export function Convert() {
  const { data: paymentInfo, isPending: isPaymentInfoPending } =
    usePaymentInfo()
  const { data: getOffersResponse, isPending: isOffersPending } = useGetOffers()
  const allValidOffers = getOffersResponse?.categories.flatMap(category =>
    category.offers.filter(isValid),
  )
  const currentProductCode = paymentInfo?.productCode
  const matchingProductCode = allValidOffers?.find(
    offer => offer.productCode === currentProductCode,
  )
  const selectedOffer = matchingProductCode ?? allValidOffers?.at(0)

  const offerState = useOfferState(
    selectedOffer?.productCode,
    RecurringSubscriptionInterval.YEARLY_RECURRING,
  )
  const isOfferStatePending = offerState.productCode && offerState.isPending

  if (isPaymentInfoPending || isOffersPending || isOfferStatePending) {
    return <LoadingOverlay id="Convert" open />
  }

  return <ConvertPure offerState={offerState} />
}
